import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import CardInfo from './CardInfo';

/**
 * SalesVelocity — bar-list against the p90 peak velocity benchmark.
 * Mirrors the BarChartWidget pattern (no chart lib). Reads `data.salesVelocity`.
 */
export default function SalesVelocity({ velocity }) {
    const { t } = useTranslation();
    const benchmark = velocity?.p90PeakPerSqft || 0;
    const tenants = Array.isArray(velocity?.tenants) ? velocity.tenants : [];

    const max = useMemo(() => {
        const peaks = tenants.map(t => t.peak_velocity || 0);
        return Math.max(benchmark, ...peaks, 1);
    }, [tenants, benchmark]);

    return (
        <div className="v3-card v3-cc-card">
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <h3 style={{ margin: 0 }}>{t('Sales Velocity')}</h3>
                    <CardInfo title={t('About Sales Velocity')}>
                        <p>
                            {t('This card ranks tenants by')} <strong>{t('peak sales velocity')}</strong> — {t('their best sales-per-square-foot rate, measured against a portfolio benchmark.')}
                        </p>
                        <p>
                            <strong>{t('Peak velocity')}</strong> {t('is the tenant’s highest sales-per-sqft value observed in the period.')}
                        </p>
                        <p>
                            <strong>{t('p90 / sqft')}</strong> {t('is the 90th-percentile peak velocity across all tenants — the benchmark the bars are scaled against.')}
                        </p>
                        <p>
                            {t('The percentage in brackets shows each tenant’s peak as a share of that p90 benchmark. Above 100% means the tenant out-paces the portfolio’s top decile.')}
                        </p>
                    </CardInfo>
                </div>
                <span className="v3-cc-card-meta">
                    {t('p90 / sqft')}: <strong style={{ color: 'var(--v3-text-primary)' }}>{benchmark.toFixed(2)}</strong>
                </span>
            </div>
            {tenants.length === 0 ? (
                <div className="v3-text-muted" style={{ fontSize: 13 }}>{t('No velocity data.')}</div>
            ) : (
                <div className="v3-cc-vel-list">
                    {tenants.slice(0, 10).map((t) => {
                        const v = t.peak_velocity || 0;
                        const pct = (v / max) * 100;
                        return (
                            <div key={t.tenant_id} className="v3-cc-vel-row">
                                <span className="label" title={t.name}>{t.name || '—'}</span>
                                <span className="track">
                                    <span className="fill" style={{ width: `${pct}%` }} />
                                </span>
                                <span className="val">
                                    {v.toFixed(2)}
                                    {t.share_of_p90 != null && (
                                        <span style={{ opacity: 0.6, marginLeft: 4 }}>
                                            ({Math.round((t.share_of_p90) * 100)}%)
                                        </span>
                                    )}
                                </span>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
